import React, { useCallback, useEffect } from 'react';
import { styled } from '@linaria/react';
import { useSelector } from 'react-redux';
import { COLORS, DirectionalIcon, typographyStyles } from '@livecommerce/design-system';

import NavigationDropdown from 'components-v3/NavigationDropdown/index.jsx';
import OrganizationsList from 'components-v3/OrganizationsList/index.jsx';
import { useNotifications, useOrganizations } from 'hooks/index.js';
import useOrgId from 'hooks/useOrgId.js';
import OrganizationLogo from 'components-v3/OrganizationLogo/index.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';

export const { getBodyText } = typographyStyles;

const MenuItemButton = styled.button`
  width: 100%;
  text-align: left;
  background-color: transparent;
  border: 0;
  border-radius: 6px;
  ${getBodyText(14)};
  padding: 10px 10px 10px 4px;
  white-space: nowrap;

  &:hover {
    background-color: ${COLORS.blackLight};
    color: ${COLORS.white};
  }
`;

const OrganizationMenuItemButton = styled(MenuItemButton)`
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 44px 1fr 20px;
  column-gap: 12px;
  min-width: 240px;
  cursor: pointer;
  transition: color 0.25s ease-in-out, background-color 0.25s ease-in-out;

  .organization-switcher__image {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
  }

  .organization-switcher__subtitle {
    ${getBodyText(12)};
    grid-column: 2 / 3;
    grid-row: 2 / 3;
    color: ${COLORS.greyMediumDark};
    margin: 0;
    transition: color 0.25s ease-in-out, background-color 0.25s ease-in-out;
  }

  .organization-switcher__image,
  .organization-switcher__chevron {
    transition: transform 0.25s ease-in-out;
  }

  &:hover {
    .organization-switcher__image {
      transform: translateX(4px);
    }

    .organization-switcher__chevron {
      transform: translateX(4px) rotate(90deg);
    }

    .organization-switcher__subtitle {
      color: ${COLORS.white};
    }
  }
`;

const Title = styled.p`
  margin: 0;
  max-width: 176px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  grid-column: 2 / 3;
  grid-row: ${props => props.title ? '1 / 2' : '1 / 3' };
  align-self: ${props => props.title ? 'end' : 'center' };
`;

const StyledChevron = styled(DirectionalIcon)`
  align-self: center;
  grid-column: 3 / 4;
  grid-row: 1 / 3;
`;

const PRODUCTS_SWITCHER_OFFSET = 170;
const PRODUCTS_SWITCHER_OFFSET_TOP_ALIGNMENT = 12;

const OrganizationSwitcher = ({
  isOrganizationSwitcherOpened,
  setIsOrganizationSwitcherOpened,
  setIsProductsMenuOpened,
  dropdownContentRef,
  ...props
}) => {
  const authUser = useSelector((state) => state.sessionState.authUser);
  const orgId = useOrgId();
  const membership = authUser?.membership?.(orgId);

  const { data: organizations, error } = useOrganizations();
  const { handleFooterNotification } = useNotifications();

  useEffect(() => {
    if (!error) return;
    handleFooterNotification({ title: 'Organizations not found', type: 'error' });
  }, [error, handleFooterNotification]);

  const { title, logo } = membership?.org || {};

  const handleClick = () => setIsOrganizationSwitcherOpened((prevValue) => !prevValue);

  const handleMenuItemClick = useCallback(
    ({ orgId: clickedOrgId }) => {
      if (clickedOrgId !== orgId) {
        trackingService.track({
          eventType: 'org-switcher:switch-org',
          value: clickedOrgId,
        });
      }
      setIsProductsMenuOpened(false);
    },
    [orgId, setIsProductsMenuOpened],
  );

  const dropdownVerticalOffset =
    organizations.length >= 5 ? -PRODUCTS_SWITCHER_OFFSET : -PRODUCTS_SWITCHER_OFFSET_TOP_ALIGNMENT;

  return (
    <NavigationDropdown
      isOpened={isOrganizationSwitcherOpened}
      isControlled
      dropdownGap={24}
      dropdownVerticalOffset={dropdownVerticalOffset}
      dropdownPosition="right"
      dropdownAlignment={organizations.length < 5 ? 'start' : 'end'}
      dropdownContent={
        <OrganizationsList
          ref={dropdownContentRef}
          isOpen={isOrganizationSwitcherOpened}
          handleMenuItemClick={handleMenuItemClick}
          organizations={organizations}
        />
      }
      {...props}
    >
      <OrganizationMenuItemButton type="button" onClick={handleClick}>
        <OrganizationLogo className="organization-switcher__image" orgId={orgId} logo={logo} />
        <Title title={title}>
          {title || 'Choose team'}
        </Title>
        {title && <p className="organization-switcher__subtitle">Switch team</p>}
        <StyledChevron className="organization-switcher__chevron" name="chevronMenu" direction="right" />
      </OrganizationMenuItemButton>
    </NavigationDropdown>
  );
};

export default OrganizationSwitcher;
